<script setup>
import Button from "primevue/button"
import Dialog from "primevue/dialog"
import ProgressSpinner from "primevue/progressspinner"
import { useFallbackImages } from "~/composables/useFallbackImages"

defineOptions({ inheritAttrs: false })

const { getEpisodeFallBackImage } = useFallbackImages()

const props = defineProps({
  /**
   * allow the user to click on the enlarge button to open a dialogue with full sized image */
  allowPreview: {
    default: false,
    type: Boolean,
  },
  /**
   * allow the vertical effect to happen for vertical images (images that are taller than they are wide)
   */
  allowVerticalEffect: {
    default: false,
    type: Boolean,
  },
  /**
   * alt text for the image
   */
  alt: {
    default: "",
    type: String,
  },
  /**
   * nuxt/image sizes attribute for responsive images (https://image.nuxtjs.org/components/nuxt-img/#sizes)
   * NOT WORKING
   */
  density: {
    default: "x1 x2",
    type: String,
  },
  /**
   * image format (webp, avif, jpeg, jpg, png, gif and svg)
   */
  format: {
    default: "webp",
    type: String,
  },
  /**
   * The desired height for image
   */
  height: {
    default: null,
    type: Number,
  },
  /**
   * to help with a11y
   */
  isDecorative: {
    default: false,
    type: Boolean,
  },
  /**
   * image loading type (eager or lazy)
   */
  loading: {
    default: "lazy",
    type: String,
  },
  /**
   * Maximum height for the image. If you know the height of the original, full-sized image, use it here. It is needed for the vertical effect
   */
  maxHeight: {
    default: Infinity,
    type: Number,
  },
  /**
   * Maximum width for the image. If you know the width of the original, full-sized image, use it here. It is needed for the vertical effect
   */
  maxWidth: {
    default: Infinity,
    type: Number,
  },
  /**
   * wagtail modifiers  (https://image.nuxtjs.org/components/nuxt-img/#modifiers
   * ONLY WORKS WITH WAGTAIL PROVIDER and only supporting 'focusZoom'
   */
  modifiers: {
    default: null,
    type: Object,
  },
  /**
   * @nuxt/Image provider
   */
  provider: {
    default: "wagtail",
    type: String,
  },
  /**
   * compression quality of the iamge
   */
  quality: {
    default: 80,
    type: Number,
  },
  /**
   * desired ratio of the image
   */
  ratio: {
    default: () => [3, 2],
    type: Array,
  },
  /**
   * nuxt/image sizes attribute for responsive images (https://image.nuxtjs.org/components/nuxt-img/#sizes)
   */
  sizes: {
    default: "",
    type: String,
  },
  /**
   * wagtail image id
   */
  src: {
    default: null,
    type: [String, Number, Object],
  },
  /**
   * address to navigate to when the image is clicked
   */
  to: {
    default: null,
    type: String,
  },
  /**
   *  the grayscale of the background image
   */
  verticalBgGrayscale: {
    default: "100",
    type: String,
  },
  /**
   * The desired width for image
   */
  width: {
    default: null,
    type: Number,
  },
  /**
   * The desired object fit for image
   */
  objectFit: {
    default: "cover",
    type: String,
  },
})
const emit = defineEmits([
  "image-click",
  "image-enlarge-click",
  "image-load",
  "image-error",
  "enlarge-image-load",
])

const isVertical = ref(
  props.allowVerticalEffect &&
    props.maxHeight >= props.maxWidth &&
    props.maxHeight !== Infinity &&
    props.maxWidth !== Infinity
)
const loadingEnlargedImage = ref(false)
const loadedEnlargedImage = ref(true)
const imageRef = ref(null)

const computedWidth = computed(() => {
  return isVertical.value
    ? Math.round(props.maxWidth / (props.maxHeight / props.height))
    : props.width
})
const computedHeight = computed(() => {
  return props.sizes ? null : props.height
})
const computedEnlargeWidth = computed(() => {
  if (import.meta.server) return props.maxWidth
  const modalFramePaddingOffset = 84
  return window.innerWidth * window.devicePixelRatio > props.maxWidth
    ? props.maxWidth
    : (window.innerWidth - modalFramePaddingOffset) * window.devicePixelRatio
})
const computedEnlargeHeight = computed(() => {
  if (import.meta.server) return props.maxHeight
  const originalWidth = props.maxWidth
  const originalHeight = props.maxHeight
  const newWidth = computedEnlargeWidth.value / window.devicePixelRatio
  const originalRatio = originalWidth / originalHeight

  return Math.round((newWidth / originalRatio) * window.devicePixelRatio)
})

const computedSrc = computed(() => {
  if (!props.src || props.src === "undefined" || props.src === "null") {
    return getEpisodeFallBackImage()
  }

  if (typeof props.src === "object") {
    return (
      (props.src.id != null ? String(props.src.id) : null) ||
      props.src.imageTemplate ||
      props.src.url ||
      props.src.src ||
      props.src.image ||
      props.src.file ||
      props.src.template ||
      getEpisodeFallBackImage()
    )
  }
  return String(props.src)
})
// method to handle the click on the enlarge button and its loading states
const enlarge = () => {
  loadingEnlargedImage.value = true
  loadedEnlargedImage.value = false
  emit("image-enlarge-click")
}
// method called when the imamge is loaded
const enlargeLoad = (target) => {
  emit("enlarge-image-load", target)
  loadedEnlargedImage.value = true
}

// Handle image load with better reliability for cached images
const handleImageLoad = () => {
  emit("image-load")
}

// Check if image is already loaded on mount (for cached images)
onMounted(async () => {
  await nextTick()

  if (!imageRef.value) {
    return
  }

  // The nuxt-img component renders directly as an <img> element
  const img = imageRef.value.$el

  if (img && img.tagName === "IMG" && img.complete && img.naturalHeight !== 0) {
    // Image is already loaded (cached)
    emit("image-load")
  }
})

const handleProvider = computed(() => {
  return isNaN(props.src) ? null : props.provider
})
</script>

<template>
  <div class="v-image v-image-wagtail">
    <VFlexibleLink
      raw
      :to="props.to"
      :aria-hidden="props.isDecorative ? true : false"
      :tabindex="props.isDecorative ? undefined : 0"
      :inert="props.isDecorative ? true : undefined"
      class="v-image-link"
      :class="{ 'is-decorative': props.isDecorative }"
      @click="props.to ? emit('image-click', props.to) : null"
    >
      <div
        class="v-image-holder"
        :style="`${
          props.sizes ? `` : `aspect-ratio:${ratio[0]} / ${ratio[1]}`
        }`"
      >
        <div v-if="isVertical" class="bg">
          <nuxt-img
            :format="props.format"
            :provider="handleProvider"
            class="blurred-bg-image"
            :src="computedSrc"
            :width="computedWidth"
            :height="computedHeight"
            :quality="String(props.quality)"
            :alt="props.isDecorative ? '' : props.alt + '-blurred-bg'"
            :modifiers="props.modifiers"
            :loading="props.loading"
            :preload="{
              fetchPriority: props.loading === 'eager' ? 'high' : 'low',
            }"
          />
        </div>
        <nuxt-img
          ref="imageRef"
          :format="props.format"
          :provider="handleProvider"
          :class="[
            'image native-image',
            { 'is-vertical': isVertical },
            props.objectFit ? `object-fit-${props.objectFit}` : '',
          ]"
          :src="computedSrc"
          :width="computedWidth"
          :height="computedHeight"
          :sizes="props.sizes"
          :densities="props.density"
          :style="[
            isVertical
              ? `aspect-ratio:${props.maxWidth} / ${props.maxHeight}; object-fit: contain;`
              : '',
          ]"
          :alt="props.isDecorative ? '' : props.alt"
          :quality="String(props.quality)"
          :loading="props.loading"
          :preload="{
            fetchPriority: props.loading === 'eager' ? 'high' : 'low',
          }"
          :modifiers="props.modifiers"
          @load="handleImageLoad"
          @error="emit('image-error')"
        />
        <slot class="slot caption" name="caption"></slot>
        <slot class="slot gallery" name="gallery"></slot>
        <template v-if="allowPreview">
          <div class="enlarge-button-holder">
            <slot
              class="slot enlarge-button"
              name="enlargeButton"
              :enlarge-func="enlarge"
            >
              <Button
                icon="pi pi-clone"
                class="enlarge-button"
                aria-label="Enlarge Image"
                @click.prevent="enlarge"
              ></Button>
            </slot>
          </div>
          <ClientOnly>
            <Dialog
              v-model:visible="loadingEnlargedImage"
              modal
              dismissable-mask
              :draggable="false"
              header=" "
              :style="{ width: '95vw' }"
            >
              <nuxt-img
                :format="props.format"
                :provider="handleProvider"
                class="enlarged-image"
                :src="computedSrc"
                style="width: 100%; height: auto"
                :alt="props.isDecorative ? '' : props.alt"
                loading="eager"
                :preload="{ fetchPriority: 'high' }"
                :quality="70"
                :width="computedEnlargeWidth"
                :height="computedEnlargeHeight"
                :modifiers="props.modifiers"
                @load="enlargeLoad($event.target)"
              />
              <template #closeicon
                ><slot class="slot close-icon" name="closeicon"></slot
              ></template>
            </Dialog>
            <Teleport to="body">
              <ProgressSpinner
                v-if="loadingEnlargedImage && !loadedEnlargedImage"
                style="
                  z-index: 1102;
                  position: fixed;
                  top: 0;
                  bottom: 0;
                  left: 0;
                  right: 0;
                  margin: auto;
                "
                stroke-width="6"
              />
            </Teleport>
          </ClientOnly>
        </template>
      </div>
    </VFlexibleLink>
    <slot class="slot below-image" name="belowImage"></slot>
  </div>
</template>

<style lang="scss" scoped>
.v-image-link {
  width: auto;
  height: inherit;
}
.v-image-link.is-decorative {
  pointer-events: none;
}
.v-image {
  line-height: 0;
  position: relative;
  height: inherit;

  .v-image-holder {
    position: relative;
    overflow: hidden;
    height: inherit;
    //width: -webkit-fill-available;
    .image {
      position: relative;
      width: 100%;
      height: 100%;
      top: 0;
      &.object-fit-cover {
        object-fit: cover;
      }

      &.object-fit-contain {
        object-fit: contain;
      }

      &.object-fit-fill {
        object-fit: fill;
      }

      &.object-fit-scale-down {
        object-fit: scale-down;
      }
      &.is-vertical {
        margin: auto;
        display: block;
      }
      img {
        cursor: default;
      }
    }
    .enlarge-button-holder {
      position: absolute;
      top: var(--v-image-button-padding);
      left: var(--v-image-button-padding);
      .enlarge-button {
        background-color: rgba(var(--p-primary-500-rgb), 0.8);
        border-color: transparent;
      }
    }
    .bg {
      pointer-events: none;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      overflow: hidden;
      img {
        width: 100%;
        @include v-image-bg-blur();
        object-fit: cover;
        height: inherit;
      }
    }
  }
  .slot:empty {
    display: none;
  }
}
</style>
